import { AbsolutCentered } from "AbsolutCentered/AbsolutCentered"
import { ArrowLeftIcon } from "Icons/Icon"
import { Loader } from "Loader/Loader"
import React, { FC, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { v4 } from "uuid"
import { HorizontalScrollBox } from "../../Orders/Components/HorizontalScrollBox/HorizontalScrollBox"
import { convertTextToInitials } from "../../Orders/Helpers"
import { cls } from "../cls"
import style from "./TabbedSubView.module.css"

export type TabbedSubViewProps = {
	profilePicture?: TabbedSubViewProfilePicture | null
	name: string
	infoBoxes: TabbedSubViewInfoBox[]
	menuItems: TabbedSubViewMenuItem[]
	cancelAction?: TabbedSubViewCancel | null
	loading?: boolean | null
	dontSetQueryParams?: boolean
	onBeforeQueryParamSet?: (currentParams: URLSearchParams) => URLSearchParams
	children: React.ReactNode
}

export type TabbedSubViewCancel = {
	buttonText: string
	onClick: () => void
}

export type TabbedSubViewInfoBox = {
	id: string
	text: string
	iconElement?: JSX.Element | null
	onClick?: (() => void) | null
}

export type TabbedSubViewProfilePicture = {
	showBox: boolean
	src?: string
}

export type TabbedSubViewMenuItem = {
	id: string
	queryParamIdValue?: string | null
	name: string
	disabled: boolean
	hidden: boolean
	onClick: (id: string) => void
}

export const TabbedSubView: FC<TabbedSubViewProps> = (props: TabbedSubViewProps) => {
	const componentId = useRef(v4())

	const [queryParams, setQueryParams] = useSearchParams()
	const [showProfilePictureBox, setShowProfilePictureBox] = useState<"show" | "hide" | "alt-text">(() => {
		if (props.profilePicture?.showBox === true && !!props.profilePicture?.src) {
			return "show"
		}

		return "hide"
	})

	useEffect(() => {
		setShowProfilePictureBox(() => {
			if (props.profilePicture?.showBox === true && !!props.profilePicture?.src) {
				return "show"
			}

			return "hide"
		})
	}, [props])

	const currentSelectedTab = queryParams.get("tab")

	function navBar(): JSX.Element | null {
		return (
			<div className={style.navWrapper}>
				<div className={cls(style.firstRow, { [style.noProfilePicture]: showProfilePictureBox === "hide" })}>
					{showProfilePictureBox !== "hide" ? (
						showProfilePictureBox === "show" ? (
							<div className={style.profilePicture}>
								<img
									src={props.profilePicture?.src}
									onError={(e) => {
										setShowProfilePictureBox("alt-text")
									}}
									alt=""
								/>
							</div>
						) : (
							<div className={style.profilePicture}>{convertTextToInitials(props.name)}</div>
						)
					) : null}

					<div className={style.name}>{props.name}</div>
					<div className={style.informationBoxesWrapper}>
						{props.infoBoxes.map((box) => {
							const onClick = box.onClick
							if (onClick) {
								return (
									<div
										key={componentId.current + box.id}
										className={style.informationBoxClickable}
										onClick={() => {
											onClick()
										}}>
										{box.text}
										{box.iconElement}
									</div>
								)
							} else {
								return (
									<div key={componentId.current + box.id} className={style.informationBox}>
										{box.text}
									</div>
								)
							}
						})}
					</div>
				</div>
				<div className={cls(style.secondRow, { [style.noProfilePicture]: showProfilePictureBox === "hide" })}>
					<HorizontalScrollBox
						className={style.horizontalScrollWrapper}
						cellClassName={style.horizontalScroll}
						key={`tabbedSubViewMenuItems-${componentId.current}`}>
						{props.menuItems.map((menuItem) => {
							if (menuItem.hidden) {
								return null
							}
							const tabId = menuItem.queryParamIdValue || menuItem.id
							return (
								<div
									key={componentId.current + menuItem.id}
									className={cls(
										style.item,
										{
											[style.selectedItem]: currentSelectedTab === tabId,
										},
										{
											[style.disabledItem]: menuItem.disabled,
										},
									)}
									onClick={() => {
										if (queryParams.get("tab") !== tabId) {
											if (props.dontSetQueryParams !== true) {
												queryParams.set("tab", tabId)

												if (props.onBeforeQueryParamSet) {
													const finalParams = props.onBeforeQueryParamSet(queryParams)
													setQueryParams(finalParams)
												} else {
													setQueryParams(queryParams)
												}
											}
											menuItem.onClick(menuItem.id)
										}
									}}>
									{menuItem.name}
								</div>
							)
						})}
					</HorizontalScrollBox>
				</div>
			</div>
		)
	}

	return (
		<div className={cls(style.wrapper)}>
			{props.cancelAction ? (
				<div
					className={style.cancelButton}
					onClick={() => {
						queryParams.delete("tab")
						setQueryParams(queryParams)
						props.cancelAction?.onClick()
					}}>
					<ArrowLeftIcon size={28} className={style.text} />
					<div className={style.text}>
						<strong>{props.cancelAction.buttonText}</strong>
					</div>
				</div>
			) : null}
			{navBar()}
			<div className={style.mainArea}>
				{props.loading === true ? (
					<AbsolutCentered>
						<Loader />
					</AbsolutCentered>
				) : (
					props.children
				)}
			</div>
		</div>
	)
}
